/**
 * BatchPanel — Right-panel tab that orchestrates batch execution.
 *
 * Flow (single component, sectioned):
 *   1. Toggle "Modo Lote"
 *   2. Configuración del flujo: marcar nodo INICIO (uploadImage) + nodo FIN.
 *      Validación en vivo (conectividad, tipo, ciclos).
 *   3. Fuentes: agregar imágenes (upload directo o desde galería).
 *   4. Estimación: N imágenes × M nodos = X ejecuciones · Y créditos.
 *   5. Ejecutar (con modal de confirmación).
 *   6. Progreso en vivo durante el run.
 *   7. Resultados: grilla por item con Ver / Descargar / Timeline.
 */
import { useEffect, useMemo, useState } from 'react';
import { Layers, Play, X, Check, AlertTriangle, Loader2, Trash2, Eye, Download, Upload, Image as ImageIcon, Info, RotateCcw } from 'lucide-react';
import { useBatchStore } from '../../store/batchStore';
import { useWorkflowStore } from '../../store/workflowStore';
import { useCreditsStore } from '../../store/creditsStore';
import { useBatchRunner } from '../../hooks/useBatchRunner';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { uploadToR2 } from '../../utils/uploadToR2';
import { downloadFile } from '../../utils/downloadFile';
import BatchGalleryPicker from './BatchGalleryPicker';
import { toast } from 'sonner';

const BatchPanel = () => {
  const batchMode = useBatchStore((s) => s.batchMode);
  const startNodeId = useBatchStore((s) => s.startNodeId);
  const endNodeId = useBatchStore((s) => s.endNodeId);
  const pickingMode = useBatchStore((s) => s.pickingMode);
  const involvedNodeIds = useBatchStore((s) => s.involvedNodeIds);
  const processQueue = useBatchStore((s) => s.processQueue);
  const flowValid = useBatchStore((s) => s.flowValid);
  const flowError = useBatchStore((s) => s.flowError);
  const sources = useBatchStore((s) => s.sources);
  const status = useBatchStore((s) => s.status);
  const items = useBatchStore((s) => s.items);
  const currentIndex = useBatchStore((s) => s.currentIndex);
  const currentNodeId = useBatchStore((s) => s.currentNodeId);
  const activeBatchId = useBatchStore((s) => s.activeBatchId);

  const nodes = useWorkflowStore((s) => s.nodes);
  const edges = useWorkflowStore((s) => s.edges);

  const balance = useCreditsStore((s) => s.balance);

  const { runBatch, cancelBatch, retryFailedItems } = useBatchRunner();
  const { openImagePreview, openVideoPreview } = useMediaPreview();

  const [showConfirm, setShowConfirm] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [uploading, setUploading] = useState(false);

  // beforeunload guard — warn the user if they try to close the tab
  // while a batch is running. Cooperative only (browser shows generic
  // dialog; message text is ignored on modern browsers).
  useEffect(() => {
    if (status !== 'running') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [status]);

  // Recompute flow whenever nodes/edges change (users may edit the canvas
  // between picking start and end, or after a run finishes).
  useEffect(() => {
    if (startNodeId || endNodeId) {
      useBatchStore.getState().recomputeFlow();
    }
  }, [nodes, edges, startNodeId, endNodeId]);

  const isRunning = status === 'running';

  // ─── Cost estimation (async: Envío 4 cotiza con los params del nodo) ────
  const [estimate, setEstimate] = useState({
    perItem: 0, itemCount: 0, total: 0, executionCount: 0, breakdown: [],
  });
  useEffect(() => {
    let alive = true;
    useBatchStore.getState().getEstimatedCost().then((e) => {
      if (alive && e) setEstimate(e);
    });
    return () => { alive = false; };
  }, [processQueue, sources, nodes]);
  const insufficient = balance !== null && estimate.total > balance;

  // ─── Handlers ────────────────────────────────────
  const nameOf = (nodeId) => {
    const n = nodes.find((x) => x.id === nodeId);
    if (!n) return '—';
    return `${nodeId} · ${n.type}`;
  };

  const handlePickStart = () => {
    useBatchStore.getState().setPickingMode(pickingMode === 'start' ? null : 'start');
  };
  const handlePickEnd = () => {
    useBatchStore.getState().setPickingMode(pickingMode === 'end' ? null : 'end');
  };
  const handleClearFlow = () => useBatchStore.getState().clearFlow();

  const handleUploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const file of Array.from(fileList)) {
      try {
        const { url } = await uploadToR2(file);
        uploaded.push({ url, name: file.name });
      } catch (e) {
        toast.error(`Falló la subida de ${file.name}: ${e.message}`);
      }
    }
    if (uploaded.length) {
      useBatchStore.getState().addSources(uploaded);
      toast.success(`${uploaded.length} imagen(es) agregadas al lote.`);
    }
    setUploading(false);
  };

  const handleGalleryOpen = () => setShowGallery(true);

  const handleRetryFailed = async () => {
    try {
      await retryFailedItems();
      const s = useBatchStore.getState();
      const ok = s.items.filter((it) => it.status === 'completed').length;
      const bad = s.items.filter((it) => it.status === 'failed').length;
      toast.success(`Reintento completado: ${ok} OK · ${bad} fallidos.`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleRemoveSource = (i) => useBatchStore.getState().removeSource(i);
  const handleClearSources = () => useBatchStore.getState().clearSources();

  const canRun = flowValid && sources.length > 0 && !isRunning && !insufficient;

  const handleConfirmRun = async () => {
    setShowConfirm(false);
    try {
      await runBatch();
      const s = useBatchStore.getState();
      const okCount = s.items.filter((it) => it.status === 'completed').length;
      const failCount = s.items.filter((it) => it.status === 'failed').length;
      toast.success(`Lote finalizado: ${okCount} OK, ${failCount} fallidos.`);
    } catch (e) {
      toast.error(`Batch: ${e.message}`);
    }
  };

  const handleResetRuntime = () => useBatchStore.getState().resetRuntime();

  // ─── Rendering ────────────────────────────────────
  return (
    <div className="batch-panel" data-testid="batch-panel">
      {/* Header + toggle */}
      <div className="batch-panel-header">
        <div className="batch-title">
          <Layers size={16} />
          <span>Ejecución por Lote</span>
        </div>
        <label className="batch-toggle">
          <input
            type="checkbox"
            checked={batchMode}
            onChange={(e) => useBatchStore.getState().setBatchMode(e.target.checked)}
            data-testid="batch-mode-toggle"
          />
          <span>{batchMode ? 'Activo' : 'Inactivo'}</span>
        </label>
      </div>

      {!batchMode ? (
        <div className="batch-empty-state">
          <p>
            Activá el modo lote para correr el mismo flujo con múltiples imágenes iniciales.
            Marcá el nodo de <b>INICIO</b> (Upload Image) y el nodo de <b>FIN</b>, cargá las
            fuentes y ejecutá.
          </p>
        </div>
      ) : (
        <>
          {/* Configuración del flujo */}
          <section className="batch-section">
            <h4>1 · Configuración del flujo</h4>
            <div className="batch-row">
              <span className="batch-label">Inicio (Upload Image):</span>
              <div className="batch-value">
                {startNodeId ? (
                  <span className="batch-node-chip start">{nameOf(startNodeId)}</span>
                ) : (
                  <span className="batch-node-none">—</span>
                )}
                <button
                  className={`batch-pick-btn ${pickingMode === 'start' ? 'active' : ''}`}
                  onClick={handlePickStart}
                  disabled={isRunning}
                  data-testid="batch-pick-start"
                >
                  {pickingMode === 'start' ? 'Click en un nodo…' : startNodeId ? 'Cambiar' : 'Marcar INICIO'}
                </button>
              </div>
            </div>
            <div className="batch-row">
              <span className="batch-label">Fin:</span>
              <div className="batch-value">
                {endNodeId ? (
                  <span className="batch-node-chip end">{nameOf(endNodeId)}</span>
                ) : (
                  <span className="batch-node-none">—</span>
                )}
                <button
                  className={`batch-pick-btn ${pickingMode === 'end' ? 'active' : ''}`}
                  onClick={handlePickEnd}
                  disabled={isRunning}
                  data-testid="batch-pick-end"
                >
                  {pickingMode === 'end' ? 'Click en un nodo…' : endNodeId ? 'Cambiar' : 'Marcar FIN'}
                </button>
              </div>
            </div>

            {(startNodeId || endNodeId) && (
              <div className={`batch-flow-status ${flowValid ? 'valid' : 'invalid'}`}>
                {flowValid ? (
                  <>
                    <Check size={14} />
                    <span>Flujo válido · {processQueue.length} nodo(s) procesable(s) · {involvedNodeIds.length} nodo(s) involucrado(s)</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle size={14} />
                    <span>{flowError}</span>
                  </>
                )}
                <button className="batch-clear-flow" onClick={handleClearFlow} disabled={isRunning} data-testid="batch-clear-flow">
                  <X size={12} /> Limpiar
                </button>
              </div>
            )}
          </section>

          {/* Fuentes */}
          <section className="batch-section">
            <h4>2 · Fuentes ({sources.length})</h4>
            <div className="batch-source-actions">
              <label className="batch-upload-btn" data-testid="batch-upload-label">
                <Upload size={14} />
                <span>{uploading ? 'Subiendo…' : 'Subir imágenes'}</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  disabled={uploading || isRunning}
                  onChange={(e) => handleUploadFiles(e.target.files)}
                  data-testid="batch-upload-input"
                />
              </label>
              <button
                className="batch-gallery-btn"
                onClick={handleGalleryOpen}
                disabled={isRunning}
                data-testid="batch-gallery-btn"
              >
                <ImageIcon size={14} /> Desde galería
              </button>
              {sources.length > 0 && (
                <button
                  className="batch-clear-sources"
                  onClick={handleClearSources}
                  disabled={isRunning}
                  data-testid="batch-clear-sources"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
            {sources.length === 0 ? (
              <div className="batch-empty-sources">Sin imágenes cargadas.</div>
            ) : (
              <ul className="batch-source-list">
                {sources.map((s, i) => (
                  <li key={s.id || s.url} className="batch-source-item">
                    <img src={s.url} alt={s.name} />
                    <span className="batch-source-name">{s.name}</span>
                    <button
                      className="batch-source-remove"
                      onClick={() => handleRemoveSource(i)}
                      disabled={isRunning}
                    >
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Estimación + botón Ejecutar */}
          {flowValid && sources.length > 0 && (
            <section className="batch-section">
              <h4>3 · Estimación</h4>
              <div className="batch-estimate">
                <div className="batch-est-row">
                  <span>Imágenes × Nodos:</span>
                  <b>{sources.length} × {processQueue.length} = {estimate.executionCount} ejecuciones</b>
                </div>
                <div className="batch-est-row">
                  <span>Costo por item:</span>
                  <b>{estimate.perItem} créditos</b>
                </div>
                <div className={`batch-est-row total ${insufficient ? 'bad' : ''}`}>
                  <span>Costo total:</span>
                  <b>{estimate.total} créditos</b>
                </div>
                <div className="batch-est-row">
                  <span>Tu balance:</span>
                  <b>{balance ?? '…'}</b>
                </div>
                {insufficient && (
                  <div className="batch-warning">
                    <AlertTriangle size={12} /> Créditos insuficientes para completar el lote.
                  </div>
                )}
              </div>

              <button
                className="batch-run-btn"
                disabled={!canRun}
                onClick={() => setShowConfirm(true)}
                data-testid="batch-run-btn"
              >
                <Play size={14} /> Ejecutar Lote
              </button>
            </section>
          )}

          {/* Progreso */}
          {isRunning && (
            <section className="batch-section batch-progress" data-testid="batch-progress">
              <h4>Ejecutando…</h4>
              <div className="batch-progress-current">
                {items[currentIndex]?.sourceUrl && (
                  <img
                    src={items[currentIndex].sourceUrl}
                    alt={items[currentIndex].sourceLabel || 'Ítem actual'}
                    className="batch-progress-thumb"
                    data-testid="batch-progress-thumb"
                  />
                )}
                <div className="batch-progress-details">
                  <div className="batch-progress-row">
                    <Loader2 size={14} className="spin" />
                    <span>Ítem {Math.max(0, currentIndex) + 1} de {items.length}</span>
                  </div>
                  {items[currentIndex]?.sourceLabel && (
                    <span className="batch-progress-label" data-testid="batch-progress-label">
                      {items[currentIndex].sourceLabel}
                    </span>
                  )}
                  {currentNodeId && (
                    <div className="batch-progress-node">Nodo actual: {currentNodeId}</div>
                  )}
                </div>
              </div>
              <button className="batch-cancel-btn" onClick={cancelBatch}>
                <X size={12} /> Cancelar lote
              </button>
            </section>
          )}

          {/* Resultados */}
          {items.length > 0 && !isRunning && (
            <section className="batch-section">
              <div className="batch-results-header">
                <h4>Resultados ({activeBatchId ? activeBatchId.slice(0, 8) : '—'}…)</h4>
                <div className="batch-results-header-actions">
                  {items.some((it) => it.status === 'failed') && (
                    <button
                      className="batch-retry-failed"
                      onClick={handleRetryFailed}
                      title="Reintentar solo los items fallidos"
                      data-testid="batch-retry-failed"
                    >
                      <RotateCcw size={12} /> Reintentar fallidos
                    </button>
                  )}
                  <button className="batch-reset-runtime" onClick={handleResetRuntime}>
                    <Trash2 size={12} /> Limpiar
                  </button>
                </div>
              </div>
              <ul className="batch-results-grid" data-testid="batch-results-grid">
                {items.map((it) => (
                  <li key={it.index} className={`batch-result-item ${it.status}`}>
                    {it.finalOutputUrl ? (
                      /\.(mp4|webm|mov)(\?|$)/i.test(it.finalOutputUrl) ? (
                        <video src={it.finalOutputUrl} muted preload="metadata" />
                      ) : (
                        <img src={it.finalOutputUrl} alt={it.sourceLabel} />
                      )
                    ) : (
                      <div className="batch-result-placeholder">
                        {it.status === 'failed' ? <AlertTriangle size={20} /> : <ImageIcon size={20} />}
                      </div>
                    )}
                    <div className="batch-result-meta">
                      <span className="batch-result-name">{it.sourceLabel}</span>
                      <span className={`batch-result-badge ${it.status}`}>{it.status}</span>
                    </div>
                    {it.error && <div className="batch-result-error" title={it.error}>{it.error}</div>}
                    {it.finalOutputUrl && (
                      <div className="batch-result-actions">
                        <button
                          onClick={() => {
                            const isVid = /\.(mp4|webm|mov)(\?|$)/i.test(it.finalOutputUrl);
                            if (isVid) openVideoPreview(it.finalOutputUrl, it.sourceLabel);
                            else openImagePreview(it.finalOutputUrl, it.sourceLabel);
                          }}
                          title="Ver"
                        >
                          <Eye size={12} />
                        </button>
                        <button
                          onClick={() => downloadFile(it.finalOutputUrl, `${it.sourceLabel}_${it.index}.${/\.(mp4)(\?|$)/i.test(it.finalOutputUrl) ? 'mp4' : 'jpg'}`)}
                          title="Descargar"
                        >
                          <Download size={12} />
                        </button>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="batch-confirm-backdrop" onClick={() => setShowConfirm(false)}>
          <div className="batch-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirmar ejecución del lote</h3>
            <p><Info size={12} /> Vas a ejecutar el flujo <b>{sources.length}</b> vez/veces.</p>
            <ul className="batch-confirm-summary">
              <li>Imágenes: <b>{sources.length}</b></li>
              <li>Nodos por item: <b>{processQueue.length}</b></li>
              <li>Ejecuciones totales: <b>{estimate.executionCount}</b></li>
              <li>Costo total: <b>{estimate.total} créditos</b></li>
              <li>Tu balance: <b>{balance ?? '…'}</b> → post-lote: <b>{balance !== null ? balance - estimate.total : '…'}</b></li>
            </ul>
            <div className="batch-confirm-actions">
              <button onClick={() => setShowConfirm(false)}>Cancelar</button>
              <button className="primary" onClick={handleConfirmRun} data-testid="batch-confirm-run">
                <Play size={12} /> Ejecutar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Gallery multi-picker modal */}
      {showGallery && (
        <BatchGalleryPicker onClose={() => setShowGallery(false)} />
      )}
    </div>
  );
};

export default BatchPanel;
